import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";

import BArrow from "../../../compoment/icon/BArrow";
import Txt from "./../../../assets/img/txt.png";
import { getPortraitViewportContainer } from "../../../compoment/PortraitStage";

import UsernameModal from "../../../modal/UsernameModal";
import CanConfirmationModal from "../../../modal/CanConfirmationModal";

import { splitName } from "../../../utils";
import { GraffitiCan, getGraffitiSprayCansApi } from "../../../api";
import { useSocket } from "../../../context/SocketContext";
import Loader from "../../../compoment/ui/Loader";
import { toast } from "react-toastify";
import { updateTicketNamesApi } from "../../../services/nameUpdateServices";

const CanSelection = () => {
  const navigate = useNavigate();
  const { socket, subscribed } = useSocket();

  // State
  const [userName, setUserName] = useState("");
  const [userNameError, setUserNameError] = useState("");

  const [nameModal, setNameModal] = useState(true)
  const [name, setName] = useState('')
  const [nameErr, setNameErr] = useState('')


  const [isNameModalOpen, setIsNameModalOpen] = useState(true);
  const [isCanModalOpen, setIsCanModalOpen] = useState(false);
  const [selectedCan, setSelectedCan] = useState<GraffitiCan>();
  const [ticketDetail, setTicketDetail] = useState<any>();
  const [cans, setCans] = useState<GraffitiCan[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const pendingStatusesRef = useRef<Map<string | number, any>>(new Map());
  const [ticketData, setTicketData] = useState<any>({});


  const getCodeKey = (obj: any): string | number | undefined =>
    obj?.code ?? obj?.serialNumber ?? obj?.id;

  // get ticket detail
  useEffect(() => {
    const storedData = localStorage.getItem("ticket_detail");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setTicketData(parsedData);
        setTicketDetail(parsedData);
      } catch (error) {
        console.error("Error parsing ticket_detail from localStorage:", error);
      }
    }
  }, []);

  // Sync ticketDetail when ticketData changes
  useEffect(() => {
    if (ticketData && Object.keys(ticketData).length > 0) {
      setTicketDetail(ticketData);
    }
  }, [ticketData]);

  useEffect(() => {
    const navigationState = window.history.state;
    if (ticketData && ticketData?.first_name && ticketData?.last_name) {
      const fullName = `${ticketData?.first_name} ${ticketData?.last_name}`;
      setName(fullName);
      setNameModal(false);
    } else if (navigationState?.userName) {
      setName(navigationState.userName);
      setNameModal(false);
    } else {
      setNameModal(true);
    }
  }, [ticketData, ticketData?.first_name, ticketData?.last_name]);

  // handle username submit
  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameErr("Name field is required.");
      return;
    }
    setNameErr("");
    const { firstName, lastName } = splitName(name);

    if (!ticketData?.id) {
      toast.error("Invalid ticket data.");
      return;
    }

    try {
      setLoading(true);
      const response = await updateTicketNamesApi(ticketData.id, {
        first_name: firstName,
        last_name: lastName || " ",
      });

      if (response?.success) {
        const updatedTicket = {
          ...ticketData,
          first_name: firstName,
          last_name: lastName || " ",
        };
        setTicketData(updatedTicket);
        localStorage.setItem("ticket_detail", JSON.stringify(updatedTicket));
        toast.success("Ticket name updated successfully.");
      } else {
        console.error("Failed to update ticket names:", response?.error);
        toast.error(response?.message || "Error updating ticket names.");
      }
    } catch (error) {
      console.error("Error updating ticket names:", error);
      toast.error("Something went wrong while updating the ticket name.");
    } finally {
      setLoading(false);
      setNameModal(false);
    }
  };
  const handleCanClick = (can: GraffitiCan, index: number | 0) => {
    if (!can) return;
    if (can.status && can.status !== "free") return;
    setSelectedCan(can);
    setIsCanModalOpen(true);
  };

  const handleCanSelected = () => {
    if (!selectedCan) return;
    // Update local state
    const next = cans.map((c) => {
      if (getCodeKey(c) === getCodeKey(selectedCan)) {
        return { ...c, status: "in_use" };
      }
      return c;
    });
    setCans(next);

    try {
      localStorage.setItem("selectedCan", JSON.stringify(selectedCan));
    } catch (_) { }

    // Emit full array to backend (consistent shape)
    const selectedKey = getCodeKey(selectedCan);
    const payloadArray = next.map((c) => {
      const code = getCodeKey(c);
      if (code === selectedKey) {
        return {
          code,
          status: c.status,
          ticket: ticketData && Object.keys(ticketData).length > 0 ? ticketData : undefined,
        };
      }
      return {
        code,
        status: c.status,
      };
    });
    if (socket && subscribed) {
      socket.emit("updateSprayCansStatus", payloadArray);
    }

    navigate("/digital-graffiti-art");
  };

  // Load cans on mount and persist
  useEffect(() => {
    const loadCans = async () => {

      try {
        let cachedTicket: any = null;
        try {
          cachedTicket = ticketData;
          if (
            cachedTicket &&
            typeof cachedTicket === "object" &&
            (!cachedTicket.first_name || !cachedTicket.last_name)
          ) {
            setIsNameModalOpen(true);
          }
          setTicketDetail(cachedTicket);
        } catch (_) {
          cachedTicket = null;
        }

        const response: any = await getGraffitiSprayCansApi();
        const apiCans: GraffitiCan[] = response?.data?.data || response?.data || [];
        setCans(apiCans);
      } catch (e) {
      } finally {
      }
    };

    loadCans();
  }, []);

  useEffect(() => {
    if (!socket || !subscribed) return;
    try {
      setSyncing(true);
      socket.emit("subscribeToSprayCansStatus");
      socket.emit("getSprayCansStatus");
      // If you prefer a failsafe timeout, uncomment below:
      // const to = setTimeout(() => setSyncing(false), 10000);
      // return () => clearTimeout(to);
    } catch (_) { }
  }, [socket, subscribed]);

  // Normalize deep string payloads
  const deepParse = useMemo(() => {
    const looksJson = (s: string) => {
      const t = s.trim();
      return (t.startsWith("[") && t.endsWith("]")) || (t.startsWith("{") && t.endsWith("}"));
    };
    return (val: any): any => {
      let current = val;
      let safety = 0;
      while (typeof current === "string" && looksJson(current) && safety < 3) {
        try {
          current = JSON.parse(current);
        } catch {
          break;
        }
        safety += 1;
      }
      return current;
    };
  }, []);

  // Listen for real-time can status updates (array of cans)
  useEffect(() => {
    if (!socket) return;

    const handler = (msg: any) => {
      try {
        let payload = deepParse(msg);
        if (payload && typeof payload === "object" && (payload as any).data !== undefined) {
          payload = deepParse((payload as any).data);
        }
        if (!Array.isArray(payload)) return;

        // Build status map and cache it
        const statusByCode = new Map<string | number, any>();
        for (const u of payload) {
          const k = getCodeKey(u);
          if (k == null) continue;
          statusByCode.set(k, u?.status);
        }
        pendingStatusesRef.current = statusByCode;

        // Merge statuses by code if cans already loaded
        setCans((prev) => {
          if (!prev || !prev.length) return prev;
          let changed = false;
          const next = prev.map((c) => {
            const k = getCodeKey(c);
            if (k != null && pendingStatusesRef.current.has(k)) {
              const newStatus = pendingStatusesRef.current.get(k);
              if (newStatus !== c.status) {
                changed = true;
                return { ...c, status: newStatus } as GraffitiCan;
              }
            }
            return c;
          });
          return changed ? next : prev;
        });

        // First successful update ends syncing
        setSyncing(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log("Failed to process sprayCansStatusUpdate", e);
      }
    };

    socket.on("sprayCansStatusUpdate", handler);
    return () => {
      socket.off("sprayCansStatusUpdate", handler);
    };
  }, [socket, deepParse]);

  // When cans list loads/changes, reconcile any cached socket statuses
  useEffect(() => {
    if (!cans || !cans.length) return;
    if (!pendingStatusesRef.current || pendingStatusesRef.current.size === 0) return;
    let changed = false;
    const next = cans.map((c) => {
      const k = getCodeKey(c);
      if (k != null && pendingStatusesRef.current.has(k)) {
        const newStatus = pendingStatusesRef.current.get(k);
        if (newStatus !== c.status) {
          changed = true;
          return { ...c, status: newStatus } as GraffitiCan;
        }
      }
      return c;
    });
    if (changed) {
      setCans(next);
    }
  }, [cans]);



  return (
    <>
      {createPortal(
        <div className="fullScreenBg fullScreenBg--graffiti" />,
        getPortraitViewportContainer() ?? document.body
      )}
      {(loading || syncing) && <Loader />}
      <section className="digitalGraffiteSection portraitDevice">
        <button type="button" className="backBTN" onClick={() => navigate(-1)}>
          <span>
            <BArrow />
          </span>{" "}
          Back
        </button>

        <div className="digitalGraffiteSection__content">
          <div className="digitalGraffiteSection__heading">
            <img src={Txt} alt="Digital Graffiti" />
          </div>

          <div className="digitalGraffiteSection__cans">
            {cans.length > 0 &&
              cans.map((can: GraffitiCan) => (
                <div
                  key={can.serialNumber}
                  className={`singleCanSection ${(can?.status && can?.status !== "free") ? "unavailable" : ""}`}
                  onClick={() => handleCanClick(can, can?.serialNumber)}
                >
                  <div className="singleCanSection__layers" aria-hidden="true">
                    <div className="singleCanSection__shade" />
                    <div className="singleCanSection__graffiti" />
                  </div>
                  <span className="singleCanSection__glow" aria-hidden="true" />
                  <img
                    className="singleCanSection__can"
                    src={can?.picture}
                    alt={`Can ${can.serialNumber}`}
                  />
                  <button type="button" className="btnScanNow">
                    {can?.status === "in_use"
                      ? "Selected"
                      : can?.status && can?.status !== "free"
                        ? "Unavailable"
                        : "Select"}
                  </button>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Modals */}
      {/* Username Modal */}
      {nameModal && (
        <UsernameModal
          show={nameModal}
          onHide={() => setNameModal(false)}
          handleSubmit={handleSubmit}
          name={name}
          setName={setName}
          nameErr={nameErr}
          setNameErr={setNameErr}
        />
      )}

      {isCanModalOpen && selectedCan && (
        <CanConfirmationModal
          show={isCanModalOpen}
          onHide={() => setIsCanModalOpen(false)}
          handleCanSelected={handleCanSelected}
          selectedCan={selectedCan}
        />
      )}
    </>
  );
};

export default CanSelection;
